import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { fadeInWithDelay, floatingAnimation, withReducedMotion } from '@/lib/animations'

/**
 * Hero section for landing page
 * Features animated content and CTAs
 */
export function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-purple-50 to-white dark:from-slate-900 dark:to-slate-950">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-32 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Content */}
          <motion.div
            className="flex flex-col justify-center"
            initial="hidden"
            animate="visible"
            variants={withReducedMotion(fadeInWithDelay(0.2))}
          >
            <motion.h1
              className="text-5xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-6xl md:text-7xl"
              variants={withReducedMotion(fadeInWithDelay(0.3))}
            >
              Your Aerial Journey,{' '}
              <span className="text-purple-400">Tracked</span>
            </motion.h1>

            <motion.p
              className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400 sm:text-xl"
              variants={withReducedMotion(fadeInWithDelay(0.4))}
            >
              Master your aerial skills with confidence. Track your progress,
              set goals, and watch yourself soar to new heights.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="mt-10 flex flex-col gap-4 sm:flex-row"
              variants={withReducedMotion(fadeInWithDelay(0.5))}
            >
              <Link
                to="/auth/login"
                search={{ redirect: '/app/dashboard' }}
                className="group flex min-h-[3rem] items-center justify-center gap-2 rounded-lg bg-purple-400 px-6 py-3 text-base font-semibold text-white transition-all hover:bg-purple-500 hover:shadow-lg hover:shadow-purple-400/50 sm:text-lg"
              >
                Start Tracking Free
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#for-studios"
                className="flex min-h-[3rem] items-center justify-center rounded-lg border-2 border-purple-400 px-6 py-3 text-base font-semibold text-purple-400 transition-colors hover:bg-purple-50 dark:hover:bg-purple-950/20 sm:text-lg"
              >
                For Studios & Gyms
              </a>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              className="mt-12 flex items-center gap-8"
              variants={withReducedMotion(fadeInWithDelay(0.6))}
            >
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  2,000+
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Aerialists Tracking
                </div>
              </div>
              <div className="h-12 w-px bg-slate-300 dark:bg-slate-700" />
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  150+
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Studios Worldwide
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            className="relative flex items-center justify-center"
            initial="hidden"
            animate="visible"
            variants={withReducedMotion(fadeInWithDelay(0.4))}
          >
            {/* Animated Aerial Silhouette */}
            <motion.div
              className="relative h-96 w-96"
              variants={floatingAnimation}
              animate="animate"
            >
              {/* Gradient Circle Background */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400/20 to-purple-600/20 blur-3xl" />

              {/* Silhouette Placeholder */}
              <div className="relative flex h-full w-full items-center justify-center">
                <svg
                  className="h-64 w-64 text-purple-400/30 dark:text-purple-400/20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  {/* Simple aerial silhouette shape */}
                  <path d="M12 2C12 2 8 6 8 10C8 12 10 14 12 14C14 14 16 12 16 10C16 6 12 2 12 2Z" />
                  <path d="M12 14C12 14 10 16 10 18C10 20 11 22 12 22C13 22 14 20 14 18C14 16 12 14 12 14Z" />
                </svg>
              </div>

              {/* Decorative Elements */}
              <motion.div
                className="absolute -right-4 top-1/4 h-16 w-16 rounded-full bg-purple-400/20"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5,
                }}
              />
              <motion.div
                className="absolute -left-4 bottom-1/4 h-12 w-12 rounded-full bg-purple-600/20"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1,
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          className="h-16 w-full text-white dark:text-slate-950"
          preserveAspectRatio="none"
          viewBox="0 0 1200 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,60 C200,20 400,100 600,60 C800,20 1000,100 1200,60 L1200,120 L0,120 Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </section>
  )
}
